import { useEffect, useState } from "react";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import {
  getLeaderboard,
  LeaderboardData,
  LeaderboardEntry,
} from "@/services/leaderboard/get-leaderboard";
import { LeaderboardRow } from "./leaderboard-row";
import { CountryFlag } from "@/components/ui/country-flag/country-flag";
import { RouteNames } from "@/types/route-names";
import { LoadingOverlay } from "@/components/ui/overlays/loading-overlay";

export const LeaderboardView = () => {
  const { getAccessToken } = useAuth();
  const { setViewedPlayerId, setCurrentRoute } = useSPAContext();
  const [leaderboardData, setLeaderboardData] = useState<
    LeaderboardData | undefined
  >();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    const accessToken = getAccessToken();
    if (!accessToken) return;

    setIsLoading(true);
    try {
      const response = await getLeaderboard(accessToken);
      if (response.ok && response.data) {
        setLeaderboardData(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch leaderboard:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const topThree = leaderboardData?.entries.filter((e) => e.rank <= 3) ?? [];
  const restOfEntries =
    leaderboardData?.entries.filter((e) => e.rank > 3) ?? [];

  // Sort top three by rank for podium display: 2nd, 1st, 3rd
  const [second, first, third] = [
    topThree.find((e) => e.rank === 2),
    topThree.find((e) => e.rank === 1),
    topThree.find((e) => e.rank === 3),
  ];

  return (
    <div className="w-full flex flex-col gap-3 pb-18 p-2 bg-pk-night-1/60 h-full backdrop-blur-[2px]">
      {/* Season Header */}
      <div className="flex items-center gap-2 justify-between bg-pk-gold-3 rounded-lg px-2 xs:px-4 py-1 xs:py-2 border-3 xs:border-4 border-pk-gold-5 shadow-cartoon-yellow">
        <div className="flex flex-col">
          <span className="text-pk-gray-1 font-bold text-xl xs:text-2xl leading-tight drop-shadow-sm">
            Season 0
          </span>
          <span className="text-pk-gray-1/70 text-xs xs:text-sm font-semibold hidden">
            {leaderboardData
              ? `${leaderboardData.remaining_days} days left`
              : "Loading..."}
          </span>
        </div>
        <div className="bg-pk-gold-1 hidden rounded-md px-1.5 xs:px-3 py-1 xs:py-2 flex items-center gap-2 border-2 border-pk-gold-4">
          <img
            src="/assets/icons/medal.png"
            width={24}
            height={24}
            alt="Reward"
          />
          <span className="text-pk-gray-4 font-bold text-base xs:text-lg">
            REWARD
          </span>
        </div>
      </div>

      {/* Top 3 Podium */}
      {!isLoading && topThree.length > 0 && (
        <StandardFrame variant="gold" className="">
          <div className="flex items-end justify-center gap-2 h-[140px]">
            {/* 2nd Place */}
            {second && (
              <div className="flex flex-col items-center gap-1 w-1/3">
                <div
                  onClick={() => {
                    setViewedPlayerId(second.user_id);
                    setCurrentRoute(RouteNames.PLAYER_PROFILE);
                  }}
                  className="bg-pk-gray-3 rounded-md w-full flex flex-col items-center justify-center p-2 h-[80px] border-3 border-pk-gray-4 cursor-pointer hover:bg-pk-gray-3/80 transition"
                >
                  <img
                    src="/assets/icons/medal.png"
                    width={32}
                    height={32}
                    className="grayscale"
                    alt="2nd Rank"
                  />
                  <div className="flex items-center gap-1 mt-1 w-full justify-center">
                    <CountryFlag countryCode={second.country} size="sm" />
                    <span className="text-pk-gray-5 font-bold text-xs xs:text-sm truncate">
                      {second.username}
                    </span>
                  </div>
                  <span className="text-pk-gray-4 text-xs">{second.score}</span>
                </div>
                <div className="bg-pk-gray-3 rounded-t-md w-full h-[30px] flex items-center justify-center border-3 border-b-0 border-pk-gray-4">
                  <span className="text-pk-gray-5 font-bold text-2xl">2</span>
                </div>
              </div>
            )}

            {/* 1st Place */}
            {first && (
              <div className="flex flex-col items-center gap-1 w-1/3">
                <div
                  onClick={() => {
                    setViewedPlayerId(first.user_id);
                    setCurrentRoute(RouteNames.PLAYER_PROFILE);
                  }}
                  className="bg-pk-gold-4 rounded-md w-full flex flex-col items-center justify-center p-2 h-[100px] border-3 border-pk-gold-5 cursor-pointer hover:bg-pk-gold-4/80 transition"
                >
                  <img
                    src="/assets/icons/medal.png"
                    width={40}
                    height={40}
                    alt="1st Rank"
                    className="drop-shadow-md"
                  />
                  <div className="flex items-center gap-1 mt-1 w-full justify-center">
                    <CountryFlag countryCode={first.country} size="sm" />
                    <span className="text-pk-gray-1 font-bold text-xs xs:text-sm truncate">
                      {first.username}
                    </span>
                  </div>
                  <span className="text-pk-gold-1 text-xs font-bold">
                    {first.score}
                  </span>
                </div>
                <div className="bg-pk-gold-4 rounded-t-md w-full h-[50px] flex items-center justify-center border-3 border-b-0 border-pk-gold-5">
                  <span className="text-pk-gold-1 font-bold text-3xl">1</span>
                </div>
              </div>
            )}

            {/* 3rd Place */}
            {third && (
              <div className="flex flex-col items-center gap-1 w-1/3">
                <div
                  onClick={() => {
                    setViewedPlayerId(third.user_id);
                    setCurrentRoute(RouteNames.PLAYER_PROFILE);
                  }}
                  className="bg-pk-orange-2 rounded-md w-full flex flex-col items-center justify-center p-2 h-[70px] border-3 border-pk-orange-4 cursor-pointer hover:bg-pk-orange-2/80 transition"
                >
                  <img
                    src="/assets/icons/medal.png"
                    width={28}
                    height={28}
                    className="sepia contrast-125 brightness-90"
                    alt="3rd Rank"
                  />
                  <div className="flex items-center gap-1 mt-1 w-full justify-center">
                    <CountryFlag countryCode={third.country} size="sm" />
                    <span className="text-pk-gray-5 font-bold text-xs xs:text-sm truncate">
                      {third.username}
                    </span>
                  </div>
                  <span className="text-pk-orange-4 text-xs">
                    {third.score}
                  </span>
                </div>
                <div className="bg-pk-orange-2 rounded-t-md w-full h-[20px] flex items-center justify-center border-3 border-b-0 border-pk-orange-4">
                  <span className="text-pk-orange-4 font-bold text-xl">3</span>
                </div>
              </div>
            )}
          </div>
        </StandardFrame>
      )}

      {/* Rankings List */}
      <StandardFrame
        variant="gold"
        className="flex-1 overflow-hidden relative min-h-[250px]"
      >
        {/* Loading Overlay */}
        <LoadingOverlay isVisible={isLoading} message="Loading rankings..." />

        {/* Header */}
        <div className="flex items-center gap-3 p-1 bg-pk-gold-1 border-b-2 border-pk-gold-3 z-10">
          <span className="min-w-[30px] text-pk-gray-4 text-sm font-bold">
            RANK
          </span>
          <span className="flex-1 text-pk-gray-4 text-sm font-bold">NAME</span>
          <span className="min-w-[60px] text-right text-pk-gray-4 text-sm font-bold">
            SCORE
          </span>
        </div>

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto flex flex-col gap-0.5">
          {!isLoading && restOfEntries.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-pk-gray-4">
              No rankings available
            </div>
          ) : (
            !isLoading &&
            restOfEntries.map((entry) => (
              <LeaderboardRow
                key={entry.user_id}
                entry={entry}
                isCurrentUser={
                  entry.user_id === leaderboardData?.current_user_entry?.user_id
                }
                onClick={() => {
                  setViewedPlayerId(entry.user_id);
                  setCurrentRoute(RouteNames.PLAYER_PROFILE);
                }}
              />
            ))
          )}
        </div>

        {/* Sticky Current User Row */}
        {!isLoading && leaderboardData?.current_user_entry && (
          <div className="mt-auto border-t-4 border-pk-gold-3 bg-pk-gold-1 z-10">
            <LeaderboardRow
              entry={leaderboardData.current_user_entry}
              isCurrentUser={true}
              onClick={() => {
                setViewedPlayerId(leaderboardData.current_user_entry!.user_id);
                setCurrentRoute(RouteNames.PLAYER_PROFILE);
              }}
            />
          </div>
        )}
      </StandardFrame>
    </div>
  );
};
